import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { getAllPlayers } from "../services/playersService";
import { formatPlayers } from "../models/playerModel";
import { toast } from "react-toastify";
import { PlayerDetailsViewOnly } from "../models/teamModel";
import { storeVersion, migrate, onRehydrateStorage } from "./versioning";
import { useSeasonsStore } from "./seasonsStore";

const defaultValues = {
  version: storeVersion,
  players: null,
  isBusy: false,
  busyMessage: "",
  pendingInvites: [],
};

interface PlayerStore {
  version: number;
  players: PlayerDetailsViewOnly[] | null;
  isBusy: boolean;
  busyMessage: string;
  pendingInvites: any[];
  getPlayers: (seasonId: string) => Promise<void>;
}

export const usePlayersStore = create<PlayerStore>()(
  devtools(
    persist(
      (set) => ({
        ...defaultValues,
        getPlayers: async (seasonId: string) => {
          try {
            set({ isBusy: true, busyMessage: "Fetching Players..." });
            const response = await getAllPlayers(seasonId);
            if (response.code && response.code === 4006) {
              const players = response.result;
              if (players && players.length > 0) {
                const currentSeason = useSeasonsStore.getState().currentSeason;
                if (
                  currentSeason &&
                  currentSeason.id === seasonId &&
                  currentSeason.courseConfigs &&
                  currentSeason.courseConfigs.length > 0
                ) {
                  const formattedPlayers = formatPlayers(
                    players,
                    currentSeason.courseConfigs
                  );
                  set({
                    players: formattedPlayers,
                    isBusy: false,
                    busyMessage: "",
                  });
                } else {
                  const formattedPlayers = formatPlayers(players);
                  set({
                    players: formattedPlayers,
                    isBusy: false,
                    busyMessage: "",
                  });
                }
              } else {
                set({ players: [], isBusy: false, busyMessage: "" });
              }
              return;
            } else {
              set({
                players: null,
                isBusy: false,
                busyMessage: "Error fetching Players",
              });
              toast.error(response.message);
            }
          } catch (error) {
            set({
              players: null,
              isBusy: false,
              busyMessage: "Error fetching Players",
            });
            console.error("Error fetching Player:", error);
          }
        },
      }),
      {
        name: "playerStore",
        storage: createJSONStorage(() => localStorage),
        onRehydrateStorage: onRehydrateStorage(defaultValues),
        migrate: migrate(defaultValues),
      }
    ),
    { name: "PlayerStore", enabled: process.env.NODE_ENV === "development" }
  )
);
